import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { AiGenerateDialog } from '@/components/ai-job/AiGenerateDialog';

const VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'] as const;
type Voice = (typeof VOICES)[number];

interface RegenerateTranslationActionProps {
  tourId: string;
  translationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Human-readable labels of the fields currently out of sync with the source. */
  outdatedFields: readonly string[];
  /**
   * Whether a voiceover-backed field (description/conclusion) is among the
   * outdated fields — only then is offering a voiceover regeneration useful.
   */
  canRegenerateVoiceovers: boolean;
}

/**
 * Regenerate ("sync with source") action for a tour translation. Re-translates
 * only the out-of-sync fields server-side, and optionally regenerates the
 * matching voiceovers in the same job. Routes through the shared on-demand
 * AI-job dialog so progress, background hand-off and toasts are handled for us.
 *
 * @param props - {@link RegenerateTranslationActionProps}.
 */
export function RegenerateTranslationAction({
  tourId,
  translationId,
  open,
  onOpenChange,
  outdatedFields,
  canRegenerateVoiceovers,
}: RegenerateTranslationActionProps) {
  const [regenerateVoiceovers, setRegenerateVoiceovers] = useState(false);
  const [voice, setVoice] = useState<Voice>('alloy');
  const [force, setForce] = useState(false);

  // When nothing has drifted, a plain regenerate would be a server-side no-op,
  // so force is implied and locked on — the user is here to re-translate a
  // translation that is in sync but wrong.
  const nothingOutdated = outdatedFields.length === 0;
  const effectiveForce = force || nothingOutdated;

  return (
    <AiGenerateDialog
      open={open}
      onOpenChange={onOpenChange}
      title={nothingOutdated ? 'Regenerate translation' : 'Sync translation with source'}
      submitLabel="Regenerate"
      description={
        nothingOutdated
          ? 'This translation is in sync. Force re-translate all fields to re-translate everything (use this if a translation came out wrong).'
          : 'Re-translate the fields that have changed in the source tour. Fields already in sync — including any manual edits — are left untouched.'
      }
      endpoint={`/tours/${tourId}/translations/${translationId}/regenerate`}
      invalidateKeys={[['TourTranslation', translationId], ['Translation']]}
      successMessage="Translation synced with the source tour"
      errorFallback="Failed to regenerate translation"
      buildPayload={() => ({
        regenerateVoiceovers,
        ...(regenerateVoiceovers ? { voice } : {}),
        ...(effectiveForce ? { force: true } : {}),
      })}
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium mb-1">Fields to re-translate</p>
          {outdatedFields.length > 0 ? (
            <ul className="list-disc pl-5 text-sm text-muted-foreground">
              {outdatedFields.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              This translation is in sync. Submit to force a full re-translate of
              every field (use this if a translation came out wrong).
            </p>
          )}
        </div>

        {canRegenerateVoiceovers && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={regenerateVoiceovers}
                onChange={(e) => setRegenerateVoiceovers(e.target.checked)}
              />
              Also regenerate voiceovers
            </label>

            {regenerateVoiceovers && (
              <div>
                <label
                  htmlFor="regen-voice"
                  className="block text-sm font-medium mb-1"
                >
                  Voice <span className="text-destructive">*</span>
                </label>
                <select
                  id="regen-voice"
                  className="w-full px-3 py-2 border rounded-md"
                  value={voice}
                  onChange={(e) => setVoice(e.target.value as Voice)}
                >
                  {VOICES.map((v) => (
                    <option key={v} value={v}>
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5 border-t pt-4">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={effectiveForce}
              disabled={nothingOutdated}
              onChange={(e) => setForce(e.target.checked)}
            />
            Force re-translate all fields
          </label>
          <p className="text-xs text-muted-foreground">
            Re-translate every field, even ones that are in sync. Use this to fix
            an incorrect translation (e.g. wrong language).
          </p>
        </div>
      </div>
    </AiGenerateDialog>
  );
}

// Export the icon for use in the action bar.
export const RegenerateTranslationIcon = RefreshCw;
